import { Component, DestroyRef, ElementRef, computed, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TeamListService, TeamSummary } from '../../services/team-list.service';
import { MAX_TEAMS } from '../../services/team.service';
import { TeamSelectMode } from './team-select.component';
import { MonStaticSpriteComponent } from '../mon-static-sprite/mon-static-sprite.component';
import { PlayBackRowComponent } from '../play-back-row/play-back-row.component';
import { SheetDrag } from '../../utils/swipe';

/** Matches the drawer's --dur-slower slide, after which the host may unmount us. */
const EXIT_MS = 300;
/** SheetDrag commits past a third of the travel, so this puts the dismiss at 90px. */
const DISMISS_TRAVEL_PX = 270;

@Component({
  selector: 'app-team-select-sheet',
  imports: [CommonModule, MonStaticSpriteComponent, PlayBackRowComponent],
  templateUrl: './team-select-sheet.component.html',
  styleUrls: ['../../styles/play-sheet-kit.css', './team-select-sheet.component.css'],
  host: {
    '[class.drawer]': 'isDrawer()',
    '[class.closing]': 'closing()',
  },
})
export class TeamSelectSheetComponent {
  mode = input.required<TeamSelectMode>();
  /** When set, this is the play sheet's drawer: a back row naming this destination,
   *  a grab handle, and pull-down to dismiss instead of a title + ×. */
  backLabel = input<string | null>(null);
  close = output<void>();

  private router = inject(Router);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  protected teamList = inject(TeamListService);
  readonly MAX_TEAMS = MAX_TEAMS;

  protected readonly isDrawer = computed(() => this.backLabel() !== null);
  protected readonly closing = signal(false);
  private exitTimer: ReturnType<typeof setTimeout> | null = null;
  private settleTimer: ReturnType<typeof setTimeout> | null = null;

  // Pull-down-to-dismiss on the grab handle: SheetDrag owns the gesture, this
  // paints the drawer's translateY and hands a committed drag to the exit.
  protected readonly drawerDrag = new SheetDrag({
    openDir: 'up',
    isOpen: () => true,
    measureTravel: () => DISMISS_TRAVEL_PX,
    onStart: () => {
      if (this.settleTimer) {
        clearTimeout(this.settleTimer);
        this.settleTimer = null;
      }
    },
    onEngage: () => {
      const style = this.host.nativeElement.style;
      style.animation = 'none';
      style.transition = 'none';
    },
    onDrag: (progress, travel) => {
      this.host.nativeElement.style.transform = `translateY(${(1 - progress) * travel}px)`;
    },
    onSettle: (open, engaged) => {
      if (!engaged) return;
      const style = this.host.nativeElement.style;
      style.transition = '';
      if (!open) {
        // Continue from wherever the finger left it rather than snapping to 0 first.
        style.transform = 'translateY(100%)';
        this.scheduleClose();
        return;
      }
      style.transform = 'translateY(0px)';
      this.settleTimer = setTimeout(() => {
        this.settleTimer = null;
        style.transform = '';
        style.animation = '';
      }, EXIT_MS);
    },
    onCancel: () => this.clearInlineStyles(),
  });

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      if (this.exitTimer) clearTimeout(this.exitTimer);
      if (this.settleTimer) clearTimeout(this.settleTimer);
      this.drawerDrag.destroy();
    });
  }

  /** The drawer plays its slide-down first; the plain sheet just goes. */
  requestClose(): void {
    if (!this.isDrawer()) {
      this.close.emit();
      return;
    }
    if (this.closing()) return;
    this.closing.set(true);
    this.scheduleClose();
  }

  selectRow(slot: number): void {
    this.teamList.selectTeam(slot);
    this.requestClose();
  }

  newTeam(): void {
    this.teamList.beginDraft();
    if (this.mode() === 'home') {
      this.router.navigate(['/teams']);
    }
    this.requestClose();
  }

  trackBySlot(_: number, team: TeamSummary): number {
    return team.slot;
  }

  monIdAsNumber(id: bigint): number {
    return Number(id);
  }

  private scheduleClose(): void {
    if (this.exitTimer) return;
    this.exitTimer = setTimeout(() => {
      this.exitTimer = null;
      this.close.emit();
    }, EXIT_MS);
  }

  private clearInlineStyles(): void {
    const style = this.host.nativeElement.style;
    style.transition = '';
    style.transform = '';
    style.animation = '';
  }
}
